import { Box, LinearProgress, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DeviceHardware } from '../../../../../../backendsdk';
import { MultipleSubImage } from '../../../../../../components/SubImage';
import { TrackedButton as Button } from '../../../../../../components/TrackedComponents';
import useProfile from '../../../../../../hooks/profile';
import { VideoStreamType, cameraToVideoStreamType } from '../../../../../../utils/ImageDimensions';
import ImageNA from '../../assets/image_not_available.png';
import FixedContainer from '../../ui/FixedContainer';
import { INSTRUCTIONS } from '../DeviceFeedback';
import { FrontFacingOverlay } from '../FrontFacingOverlay';
import { InCabinOverlay } from '../InCabinOverlay';
import SideCameraOverlay from '../SideCameraOverlay';

const INSTRUCTIONS_FOR_STREAM: Record<VideoStreamType, string[]> = {
    [VideoStreamType.InCabinCamera]: INSTRUCTIONS.in_cabin,
    [VideoStreamType.FrontFacingCamera]: INSTRUCTIONS.front_facing,
    [VideoStreamType.LeftBlindSpotCamera]: INSTRUCTIONS.bsd_left,
    [VideoStreamType.RightBlindSpotCamera]: INSTRUCTIONS.bsd_right,
    [VideoStreamType.BlindSpotCamera]: [],
    [VideoStreamType.RearCamera]: [],
};

const STREAM_LABEL_MAP: Record<VideoStreamType, string> = {
    [VideoStreamType.InCabinCamera]: 'in-cabin',
    [VideoStreamType.FrontFacingCamera]: 'front-facing',
    [VideoStreamType.LeftBlindSpotCamera]: 'left-blind-spot',
    [VideoStreamType.RightBlindSpotCamera]: 'right-blind-spot',
    [VideoStreamType.BlindSpotCamera]: 'camera',
    [VideoStreamType.RearCamera]: 'camera',
};

const INSTRUCTION_PREFIX_MAP = {
    [VideoStreamType.InCabinCamera]: 'setup_tool.device_feedback.in_cabin',
    [VideoStreamType.FrontFacingCamera]: 'setup_tool.device_feedback.front_facing',
    [VideoStreamType.LeftBlindSpotCamera]: 'setup_tool.device_feedback.bsd_left',
    [VideoStreamType.RightBlindSpotCamera]: 'setup_tool.device_feedback.bsd_right',
    [VideoStreamType.BlindSpotCamera]: 'setup_tool.device_feedback.bsd_right',
    [VideoStreamType.RearCamera]: 'setup_tool.device_feedback.front_facing',
};

interface CameraSelectionProps {
    availableStreams: string[];
    viewedStreams: Set<VideoStreamType>;
    onStreamChange: (stream: VideoStreamType) => void;
}

const CameraSelection: React.FC<CameraSelectionProps> = (props) => {
    const { t } = useTranslation();

    // Filter to show only unviewed streams, or all if none have been viewed
    const unviewedStreams = props.availableStreams.filter(
        (stream) => !props.viewedStreams.has(cameraToVideoStreamType(stream)),
    );

    return (
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <Typography>{t('setup_tool.device_feedback.select_camera')}</Typography>
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    flexDirection: 'row',
                    gap: 1,
                    justifyContent: 'center',
                }}
            >
                {unviewedStreams.map((stream) => {
                    return (
                        <Button
                            id={`btn-${stream}`}
                            data-testid={`btn-${stream}`}
                            key={stream}
                            onClick={() => props.onStreamChange(cameraToVideoStreamType(stream))}
                            variant="outlined"
                            color="primary"
                        >
                            {t(`setup_tool.device_feedback.cameras.${stream}`)}
                        </Button>
                    );
                })}
            </Box>
        </Box>
    );
};

interface CameraStreamViewProps {
    stream: VideoStreamType;
    availableStreams: VideoStreamType[];
    viewedStreams: Set<VideoStreamType>;
    snapshot: string;
    isError: boolean;
    imageCounter: number;
    keyCounter: number;
    hardware: DeviceHardware;
    horizontalPixel?: number;
    groundPixel?: number;
    objectOverlay: boolean;
    localeMinDistance?: number;
    isActionDisabled: boolean;
    onNoHorizonClick: () => void;
    onStreamSelected: (stream: VideoStreamType) => void;
}

export const CameraStreamView: React.FC<CameraStreamViewProps> = (props) => {
    const { stream, snapshot } = props;
    const [selectedStream, setSelectedStream] = useState<VideoStreamType | null>(null);
    const { t } = useTranslation();
    const { profile } = useProfile();
    const allStreamsAvailable = profile.customer.settings.installed_cameras.every((camera) =>
        props.availableStreams.includes(cameraToVideoStreamType(camera)),
    );
    const horizontalPixel = props.objectOverlay ? props.groundPixel : props.horizontalPixel;
    const showStream = allStreamsAvailable ? stream : selectedStream;
    const streamLabel = STREAM_LABEL_MAP[showStream || stream] || 'camera';
    const instructions = INSTRUCTIONS_FOR_STREAM[showStream || stream] || [];

    const overlayMap = {
        [VideoStreamType.InCabinCamera]: <InCabinOverlay hardware={props.hardware} />,
        [VideoStreamType.FrontFacingCamera]: <FrontFacingOverlay horizontalPixel={horizontalPixel} />,
        [VideoStreamType.LeftBlindSpotCamera]: <SideCameraOverlay horizontalPixel={horizontalPixel} side="left" />,
        [VideoStreamType.RightBlindSpotCamera]: <SideCameraOverlay horizontalPixel={horizontalPixel} side="right" />,
        [VideoStreamType.BlindSpotCamera]: <SideCameraOverlay side="right" />,
        [VideoStreamType.RearCamera]: <FrontFacingOverlay horizontalPixel={horizontalPixel} />,
    };

    return (
        <Box
            sx={{
                width: '100%',
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
        >
            <LinearProgress
                key={`${props.keyCounter}-progress`}
                variant="determinate"
                value={(1 - props.imageCounter / 20) * 100}
                sx={{ width: '100%', flexShrink: 0 }}
            />
            {!!snapshot && snapshot !== ImageNA ? (
                <Box
                    id={`${streamLabel}-image-container`}
                    data-testid={`${streamLabel}-image-container`}
                    sx={{ position: 'relative' }}
                >
                    <MultipleSubImage
                        id={`${streamLabel}-image`}
                        data-testid={`${streamLabel}-image`}
                        image={snapshot}
                        streams={[stream]}
                    />
                    {!props.isError && showStream ? overlayMap[showStream] : null}
                </Box>
            ) : null}
            {snapshot === ImageNA ? (
                <FixedContainer style={{ position: 'relative', marginBottom: '8px' }}>
                    <img
                        alt={`${streamLabel} image`}
                        id={`${stream}-sub-image`}
                        data-testid={`${stream}-sub-image`}
                        className="no-image"
                        src={snapshot}
                        style={{ width: '100%', height: '100%' }}
                    />
                </FixedContainer>
            ) : null}
            {!showStream ? (
                <CameraSelection
                    availableStreams={profile.customer.settings.installed_cameras}
                    viewedStreams={props.viewedStreams}
                    onStreamChange={(stream) => {
                        setSelectedStream(stream);
                        props.onStreamSelected(stream);
                    }}
                />
            ) : null}
            {instructions.length > 0 && showStream ? (
                <ul style={{ width: '100%', paddingLeft: '20px' }}>
                    {showStream === VideoStreamType.FrontFacingCamera && props.objectOverlay ? (
                        <li>
                            <Typography>{t('setup_tool.device_feedback.object_instructions_2')}</Typography>
                        </li>
                    ) : (
                        instructions.map((item) => {
                            return (
                                <li key={item} id={`li-${item}`} data-testid={`li-${item}`}>
                                    <Typography>{t(`${INSTRUCTION_PREFIX_MAP[showStream]}_${item}`)}</Typography>
                                </li>
                            );
                        })
                    )}
                </ul>
            ) : null}
            {showStream === VideoStreamType.FrontFacingCamera && !props.objectOverlay && !!props.localeMinDistance ? (
                <Button
                    id="btn-no-horizon"
                    data-testid="btn-no-horizon"
                    variant="contained"
                    color="primary"
                    disabled={!props.localeMinDistance || props.isActionDisabled}
                    onClick={props.onNoHorizonClick}
                >
                    {t('setup_tool.device_feedback.no_horizon')}
                </Button>
            ) : null}
        </Box>
    );
};
